import { Routes, Route, Navigate } from 'react-router-dom';
import { ProtectedRoute } from '@/components';
import { AppShell } from '@/components/layouts/AppShell';
import { AuthRoutes } from './authRoutes';
import { PersonalRoutes } from './personalRoutes';
import { CompanyRoutes } from './companyRoutes';
import { CollegeRoutes } from './collegeRoutes';
import { AdminRoutes } from './adminRoutes';
import { lazyPage } from './lazy';

const LandingPage = lazyPage(() => import('@/features/landing/LandingPage'), 'LandingPage');
const PublicProfilePage = lazyPage(
  () => import('@/features/profile/PublicProfilePage'),
  'PublicProfilePage'
);
const ProfileSetupPage = lazyPage(
  () => import('@/features/profile/ProfileSetupPage'),
  'ProfileSetupPage'
);
const UsersListPage = lazyPage(
  () => import('@/features/users/pages/UsersListPage'),
  'UsersListPage'
);
const HelpPage = lazyPage(() => import('@/features/personal/pages/HelpPage'), 'HelpPage');
const SharedDocumentPage = lazyPage(
  () => import('@/features/personal/pages/SharedDocumentPage'),
  'SharedDocumentPage'
);
const TermsOfServicePage = lazyPage(
  () => import('@/features/legal/pages/TermsOfServicePage'),
  'TermsOfServicePage'
);
const PrivacyPolicyPage = lazyPage(
  () => import('@/features/legal/pages/PrivacyPolicyPage'),
  'PrivacyPolicyPage'
);

export const AppRoutes = () => (
  <Routes>
    {/* ── Public ── */}
    <Route path="/" element={<LandingPage />} />
    <Route path="/u/:profileId" element={<PublicProfilePage />} />
    <Route path="/shared/document/:token" element={<SharedDocumentPage />} />
    <Route path="/terms" element={<TermsOfServicePage />} />
    <Route path="/privacy" element={<PrivacyPolicyPage />} />
    {AuthRoutes()}

    {/* ── Protected — no AppShell (focused onboarding flow) ── */}
    <Route
      path="/profile/setup"
      element={
        <ProtectedRoute>
          <ProfileSetupPage />
        </ProtectedRoute>
      }
    />

    {/* ── Protected — AppShell renders sidebar + header + <Outlet /> ── */}
    <Route
      element={
        <ProtectedRoute>
          <AppShell />
        </ProtectedRoute>
      }
    >
      {PersonalRoutes()}
      {CompanyRoutes()}
      {CollegeRoutes()}
      <Route path="/users" element={<UsersListPage />} />
      <Route path="/help" element={<HelpPage />} />
    </Route>

    {/* ── Admin Panel — own layout, own auth check ── */}
    {AdminRoutes()}

    {/* ── Redirects ── */}
    <Route path="/dashboard" element={<Navigate to="/personal/dashboard" replace />} />
    <Route path="/dashboard-redirect" element={<Navigate to="/personal/dashboard" replace />} />

    {/* ── Unauthorized ── */}
    <Route
      path="/unauthorized"
      element={
        <div className="min-h-screen bg-[#f5f7fb] p-4 sm:p-6">
          <div className="mx-auto flex min-h-[70vh] w-full max-w-2xl items-center justify-center">
            <div className="w-full rounded-2xl border border-[#dce7ee] bg-white p-6 text-center shadow-sm sm:p-8">
              <p className="inline-flex rounded-full border border-[#cfe3df] bg-[#f1f9f7] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#2f8f83]">
                Protected route
              </p>
              <h1 className="mt-4 text-3xl font-semibold text-[#111827] sm:text-4xl">
                Access denied
              </h1>
              <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-[#607080]">
                Your account does not have permission to view this page.
              </p>
              <a
                href="/personal/dashboard"
                className="mt-6 inline-flex rounded-xl bg-[#1f2937] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#111827]"
              >
                Back to dashboard
              </a>
            </div>
          </div>
        </div>
      }
    />
  </Routes>
);
